// The expanded state of a grouped instance's sections (grouping.js), held by the shell so it outlives the
// state surfaces that stand in for the section stack while rows load, fail or match nothing.

import { useCallback, useState } from 'react'

import { EMPTY_MEMORY, isGroupOpen, toggleGroup } from './grouping'

/**
 * @param {?{groups: Array<object>}} grouped the normalized grouped configuration; null when not grouped
 * @param {{search: string, filters: object}} query the committed query
 * @param {boolean} hasQuery whether a search or filter is active
 * @returns {{isOpen: (group: object) => boolean, toggle: (group: object) => void, token: object}} `token`
 *   changes whenever a section opens or closes, so geometry can re-measure
 */
export const useGroupExpansion = (grouped, query, hasQuery) => {
  const [memory, setMemory] = useState(EMPTY_MEMORY)
  // a fresh token for every committed search or filter change, so a toggle made during one query never
  // comes back when a later edit returns to the same text; a sort keeps the token
  const [seen, setSeen] = useState(() => ({
    search: query.search,
    filters: query.filters,
    token: {},
  }))
  if (seen.search !== query.search || seen.filters !== query.filters)
    setSeen({ search: query.search, filters: query.filters, token: {} })
  const active = hasQuery ? seen.token : null
  const isOpen = useCallback(
    group => Boolean(grouped) && isGroupOpen(memory, group, active),
    [grouped, memory, active]
  )
  const toggle = useCallback(
    group => setMemory(current => toggleGroup(current, group, active)),
    [active]
  )
  return { isOpen, toggle, token: memory }
}
